import React, { useEffect, useState } from 'react';
import { practiceScheduleAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { 
  Calendar,
  Plus,
  FileText,
  Download,
  Pencil,
  Trash2,
  Copy,
  Clock,
  MapPin,
  Shirt,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';

const DAY_TYPES = [
  { value: 'green', label: 'Green - Fundamentals, Habits', color: 'bg-[#32D74B]', description: 'Fundamentals, Habits' },
  { value: 'black', label: 'Black - PHYSICAL / ATTITUDE', color: 'bg-[#27272A]', description: 'PHYSICAL / ATTITUDE' },
  { value: 'orange', label: 'Orange - Recovery focused', color: 'bg-[#FF9500]', description: 'Recovery focused' },
];

const ATTIRE_OPTIONS = ['SHELLS', 'FULL PADS', 'HELMETS ONLY', 'SHORTS & SHELLS', 'WALK-THRU'];

export default function PracticeSchedulePage() {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createModal, setCreateModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ open: false, schedule: null });
  const navigate = useNavigate();

  // Form state for quick create
  const [formData, setFormData] = useState({
    practice_number: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    day_of_week: format(new Date(), 'EEEE').toUpperCase(),
    attire: 'SHELLS',
    field: '1',
    start_time: '3:30 PM',
    day_type: 'black',
    situational_emphasis: '',
  });

  useEffect(() => {
    loadSchedules();
  }, []);

  const loadSchedules = async () => {
    setLoading(true);
    try {
      const res = await practiceScheduleAPI.getAll();
      setSchedules(res.data);
    } catch (error) {
      toast.error('Failed to load practice schedules');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.practice_number) {
      toast.error('Practice number is required');
      return;
    }
    
    try {
      const res = await practiceScheduleAPI.create({
        ...formData,
        meetings: [],
        periods: []
      });
      toast.success('Practice schedule created');
      setCreateModal(false);
      setFormData({
        practice_number: '',
        date: format(new Date(), 'yyyy-MM-dd'),
        day_of_week: format(new Date(), 'EEEE').toUpperCase(),
        attire: 'SHELLS',
        field: '1',
        start_time: '3:30 PM',
        day_type: 'black',
        situational_emphasis: '',
      });
      // Navigate to edit page
      navigate(`/practice/${res.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create schedule');
    }
  };

  const handleDelete = async () => {
    if (!deleteModal.schedule) return;
    
    try {
      await practiceScheduleAPI.delete(deleteModal.schedule.id);
      toast.success('Practice schedule deleted');
      setDeleteModal({ open: false, schedule: null });
      loadSchedules();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete schedule');
    }
  };

  const handleDuplicate = async (schedule) => {
    try {
      const res = await practiceScheduleAPI.duplicate(schedule.id);
      toast.success('Practice schedule duplicated');
      navigate(`/practice/${res.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to duplicate schedule');
    }
  };

  const handleDownloadPDF = async (schedule) => {
    try {
      const res = await practiceScheduleAPI.getPDF(schedule.id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `practice_plan_${schedule.date}_${schedule.practice_number.replace(/\s+/g, '_').replace('#', '')}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const getDayTypeInfo = (dayType) => {
    return DAY_TYPES.find(d => d.value === dayType) || DAY_TYPES[1];
  };

  const handleDateChange = (date) => {
    const d = new Date(date + 'T12:00:00');
    setFormData({
      ...formData,
      date: date,
      day_of_week: format(d, 'EEEE').toUpperCase()
    });
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="practice-schedule-page">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <p className="text-xs text-[#71717A] uppercase tracking-wider">Practice</p>
            <h1 className="font-display text-4xl text-white tracking-tight">PRACTICE SCHEDULES</h1>
          </div>

          <Button
            onClick={() => setCreateModal(true)}
            className="bg-[#FF3B30] hover:bg-[#FF3B30]/90 text-white"
            data-testid="create-schedule-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Practice Plan
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-[#121212] border-[#27272A]">
            <CardContent className="p-4 text-center">
              <p className="font-display text-3xl text-white">{schedules.length}</p>
              <p className="text-xs text-[#71717A] uppercase tracking-wider mt-1">Total Plans</p>
            </CardContent>
          </Card>
          <Card className="bg-[#121212] border-[#27272A]">
            <CardContent className="p-4 text-center">
              <p className="font-display text-3xl text-[#32D74B]">
                {schedules.filter(s => s.day_type === 'green').length}
              </p>
              <p className="text-xs text-[#71717A] uppercase tracking-wider mt-1">Green - Fundamentals</p>
            </CardContent>
          </Card>
          <Card className="bg-[#121212] border-[#27272A]">
            <CardContent className="p-4 text-center">
              <p className="font-display text-3xl text-white">
                {schedules.filter(s => s.day_type === 'black').length}
              </p>
              <p className="text-xs text-[#71717A] uppercase tracking-wider mt-1">Black - Physical</p>
            </CardContent>
          </Card>
          <Card className="bg-[#121212] border-[#27272A]">
            <CardContent className="p-4 text-center">
              <p className="font-display text-3xl text-[#FF9500]">
                {schedules.filter(s => s.day_type === 'orange').length}
              </p>
              <p className="text-xs text-[#71717A] uppercase tracking-wider mt-1">Orange - Recovery</p>
            </CardContent>
          </Card>
        </div>

        {/* Schedules List */}
        <Card className="bg-[#121212] border-[#27272A]">
          <CardHeader className="border-b border-[#27272A]">
            <CardTitle className="text-white flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Practice Plans
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF3B30]"></div>
              </div>
            ) : schedules.length > 0 ? (
              <div className="divide-y divide-[#27272A]">
                {schedules.map((schedule) => {
                  const dayTypeInfo = getDayTypeInfo(schedule.day_type);
                  
                  return (
                    <div 
                      key={schedule.id} 
                      className="flex items-center justify-between p-4 hover:bg-[#1A1A1A] cursor-pointer group"
                      onClick={() => navigate(`/practice/${schedule.id}`)}
                    >
                      <div className="flex items-center gap-4">
                        {/* Day Type Indicator */}
                        <div className={`w-3 h-16 ${dayTypeInfo.color}`}></div>
                        
                        <div className="flex items-center gap-6">
                          {/* Date */}
                          <div className="text-center min-w-[60px]">
                            <p className="font-display text-2xl text-white">
                              {format(new Date(schedule.date + 'T12:00:00'), 'd')}
                            </p>
                            <p className="text-xs text-[#71717A] uppercase">
                              {format(new Date(schedule.date + 'T12:00:00'), 'MMM')}
                            </p>
                          </div>
                          
                          {/* Details */}
                          <div>
                            <p className="text-white font-medium text-lg">{schedule.practice_number}</p>
                            <div className="flex items-center gap-4 text-sm text-[#71717A] mt-1">
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {schedule.start_time}
                              </span>
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                Field {schedule.field}
                              </span>
                              <span className="flex items-center gap-1">
                                <Shirt className="w-3 h-3" />
                                {schedule.attire}
                              </span>
                              <span className="text-[#A1A1AA]">
                                {schedule.total_minutes} mins
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); handleDownloadPDF(schedule); }}
                          className="bg-[#32D74B]/10 text-[#32D74B] border border-[#32D74B]/30 hover:bg-[#32D74B]/20"
                          data-testid={`download-${schedule.id}`}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); handleDuplicate(schedule); }}
                          className="bg-[#007AFF]/10 text-[#007AFF] border border-[#007AFF]/30 hover:bg-[#007AFF]/20"
                          data-testid={`duplicate-${schedule.id}`}
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); setDeleteModal({ open: true, schedule }); }}
                          className="bg-[#FF3B30]/10 text-[#FF3B30] border border-[#FF3B30]/30 hover:bg-[#FF3B30]/20"
                          data-testid={`delete-${schedule.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                        <ChevronRight className="w-5 h-5 text-[#71717A] group-hover:text-white transition-colors" />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-16 text-[#71717A]">
                <FileText className="w-16 h-16 mb-4 opacity-50" />
                <p className="text-lg">No practice schedules yet</p>
                <p className="text-sm">Create your first practice plan to get started</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create Modal */}
        <Dialog open={createModal} onOpenChange={setCreateModal}>
          <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-lg">
            <DialogHeader>
              <DialogTitle>New Practice Plan</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-[#A1A1AA]">Practice Number / Title</Label>
                <Input
                  value={formData.practice_number}
                  onChange={(e) => setFormData({ ...formData, practice_number: e.target.value })}
                  placeholder="e.g., GAME WEEK #1, PITTSBURGH #2"
                  className="bg-[#0A0A0A] border-[#27272A] text-white mt-1"
                  data-testid="practice-number-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#A1A1AA]">Date</Label>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => handleDateChange(e.target.value)}
                    className="bg-[#0A0A0A] border-[#27272A] text-white mt-1"
                    data-testid="date-input"
                  />
                </div>
                <div>
                  <Label className="text-[#A1A1AA]">Day</Label>
                  <Input
                    value={formData.day_of_week}
                    readOnly
                    className="bg-[#0A0A0A] border-[#27272A] text-white mt-1 opacity-70"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#A1A1AA]">Start Time</Label>
                  <Input
                    value={formData.start_time}
                    onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                    placeholder="e.g., 3:30 PM"
                    className="bg-[#0A0A0A] border-[#27272A] text-white mt-1"
                    data-testid="start-time-input"
                  />
                </div>
                <div>
                  <Label className="text-[#A1A1AA]">Field</Label>
                  <Input
                    value={formData.field}
                    onChange={(e) => setFormData({ ...formData, field: e.target.value })}
                    placeholder="e.g., 1, 2, Main"
                    className="bg-[#0A0A0A] border-[#27272A] text-white mt-1"
                    data-testid="field-input"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-[#A1A1AA]">Attire</Label>
                <select
                  value={formData.attire}
                  onChange={(e) => setFormData({ ...formData, attire: e.target.value })}
                  className="w-full bg-[#0A0A0A] border border-[#27272A] text-white px-3 py-2 mt-1"
                  data-testid="attire-select"
                >
                  {ATTIRE_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <Label className="text-[#A1A1AA]">Day Type (Intensity)</Label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {DAY_TYPES.map((type) => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, day_type: type.value })}
                      className={`p-3 border flex flex-col items-center gap-1 transition-all ${
                        formData.day_type === type.value 
                          ? 'border-[#FF3B30] bg-[#FF3B30]/10' 
                          : 'border-[#27272A] bg-[#0A0A0A] hover:border-[#3F3F46]'
                      }`}
                    >
                      <div className={`w-6 h-6 rounded-full ${type.color}`}></div>
                      <span className="text-xs">{type.label}</span>
                      <span className="text-[10px] text-[#71717A]">{type.description}</span>
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <Label className="text-[#A1A1AA]">Situational Emphasis (Optional)</Label>
                <Input
                  value={formData.situational_emphasis}
                  onChange={(e) => setFormData({ ...formData, situational_emphasis: e.target.value })}
                  placeholder="e.g., 3rd Down, Red Zone, 2-Minute Drill"
                  className="bg-[#0A0A0A] border-[#27272A] text-white mt-1"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setCreateModal(false)}
                className="border-[#27272A] text-white hover:bg-[#1A1A1A]"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreate}
                className="bg-[#FF3B30] hover:bg-[#FF3B30]/90 text-white"
                data-testid="create-practice-btn"
              >
                Create & Edit
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Modal */}
        <Dialog open={deleteModal.open} onOpenChange={(open) => setDeleteModal({ ...deleteModal, open })}>
          <DialogContent className="bg-[#121212] border-[#27272A] text-white">
            <DialogHeader>
              <DialogTitle className="text-[#FF3B30]">Delete Practice Plan</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-[#A1A1AA]">
                Are you sure you want to delete <span className="text-white font-medium">
                  {deleteModal.schedule?.practice_number}
                </span>?
              </p>
              <p className="text-sm text-[#71717A] mt-2">
                This action cannot be undone.
              </p>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setDeleteModal({ open: false, schedule: null })}
                className="border-[#27272A] text-white hover:bg-[#1A1A1A]"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDelete}
                className="bg-[#FF3B30] hover:bg-[#FF3B30]/90 text-white"
                data-testid="confirm-delete-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
